import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { Box, FormControlLabel, Checkbox } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { GridCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from '@mui/icons-material/Business';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GridTable from "../../components/GridTable";
import TextField from '../../components/TextField';
import ActionMenu from '../../components/ActionMenu';
import Dialog from "../../components/Dialog";
import Dropdown from '../../components/Dropdown'
import Loader from '../../components/Loader'
import Button from '../../components/Button'
import { successMessage, errorMessage } from '../../components/Toaster';
import { ApiResponse, CompanyData, CreateCompanyParams, UpdateCompanyParams } from './interface'
import { getAllCompanies, updateCompany, deleteCompany, createCompany } from "./api";
import { getErrorMessage } from "../../utils/error";

const Company: React.FC = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<CompanyData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editCompany, setEditCompany] = useState<CompanyData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        update: false,
        delete: false,
        create: false,
        fetch: false,
    });

    const validationSchema = Yup.object().shape({
        companyName: Yup.string()
            .required('Company name is required')
            .min(2, 'Company name must be at least 2 characters long')
            .max(100, 'Company name cannot exceed 100 characters'),
        displayName: Yup.string()
            .required('Display name is required')
            .min(2, 'Display name must be at least 2 characters long')
            .max(100, 'Display name cannot exceed 100 characters'),
        companyLogo: Yup.string()
            .url('Company logo must be a valid URL')
            .nullable(),
        address: Yup.string()
            .max(500, 'Address cannot exceed 500 characters')
            .nullable(),
        contactNo: Yup.string()
            .matches(/^[0-9]{10}$/, 'Contact number must be a 10-digit number.')
            .nullable(),
    });

    // Columns for displaying companies
    const columns: GridColDef[] = [
        { field: "company_name", headerName: "Company Name", flex: 1 },
        { field: "display_name", headerName: "Display Name", flex: 1 },
        { field: "parent_company_name", headerName: "Parent Company Name", flex: 1 },
        { field: "contact_no", headerName: "Contact No", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: handleEdit,
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: handleDelete,
                        },
                        {
                            icon: <WarehouseOutlinedIcon color="action" />,
                            tooltip: "Sections classified as Production, Warehouse, or Dispatch - key operational units of the company.",
                            onClick: handleSections,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchCompanies = async () => {
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const companiesData: ApiResponse<CompanyData[]> = await getAllCompanies();
                if (companiesData.status && companiesData.data) {
                    setCompanies(companiesData.data);
                } else {
                    errorMessage(companiesData.message || "Failed to fetch companies.");
                }
            } catch (error) {
                console.error("Error fetching companies:", error);
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred. Please try again.');
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchCompanies();
    }, []);

    // Open dialog to add or edit company
    const openDialog = () => {
        setDialogOpen(true);
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditCompany(null);
        setDialogOpen(false);
    };

    // Handle edit company
    const handleEdit = (params: GridCellParams) => {
        setEditCompany(params.row as CompanyData);
        openDialog();
    };

    // Handle delete company
    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleSections = (params: GridCellParams) => {
        navigate(`/company/${params.row.company_id}/sections`, {
            state: { company_name: params.row.company_name },
        });
    };

    // Confirm dialog action (create or update company)
    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        if (editCompany) {
            await handleUpdate(values);
        } else {
            await handleCreate(values);
        }
        closeDialog(resetForm);
    };

    // Create Company
    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            console.log(values, "valuesvaluesvalues");

            const newCompany: CreateCompanyParams = {
                company_name: values.companyName.trim(),
                display_name: values.displayName.trim(),
                company_logo: values?.companyLogo ? values.companyLogo.trim() : null,
                gst_number: values?.gstNumber ? values.gstNumber.trim() : null,
                address: values?.address ? values.address.trim() : null,
                contact_no: values?.contactNo ? values.contactNo.trim() : null,
                parent_company_id: values?.parentCompanyId || null,
            };
            const response = await createCompany(newCompany);

            if (response?.status) {
                const parent_company = companies.find((c) => c.company_id === values.parentCompanyId);
                const newCompany = {
                    company_id: response?.data?.company_id,
                    company_name: values.companyName,
                    display_name: values.displayName,
                    company_logo: values.companyLogo,
                    gst_number: values.gstNumber,
                    address: values.address,
                    contact_no: values.contactNo,
                    parent_company_id: values.parentCompanyId,
                    parent_company_name: parent_company?.company_name || null,
                };
                setCompanies((prevCompanies) => {
                    const currentCompanies = Array.isArray(prevCompanies) ? prevCompanies : [];
                    return [...currentCompanies, newCompany];
                });
                successMessage(response.message || "Company created successfully!");
            } else {
                errorMessage(response?.message || "Failed to create company. Please try again.");
            }
        } catch (error) {
            console.error("Error creating company:", error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    // Update Company
    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedCompanyData: UpdateCompanyParams = {
                company_id: editCompany!.company_id,
                company_name: values.companyName.trim(),
                display_name: values.displayName.trim(),
                company_logo: values.companyLogo?.trim(),
                gst_number: values.gstNumber?.trim(),
                address: values.address?.trim(),
                contact_no: values.contactNo?.trim(),
                parent_company_id: values.parentCompanyId,
            };

            const response = await updateCompany(updatedCompanyData);

            if (response?.status) {
                const parent_company = companies.find((c) => c.company_id === values.parentCompanyId);
                setCompanies((prevCompanies) =>
                    prevCompanies.map((company) =>
                        company.company_id === updatedCompanyData.company_id
                            ? { ...company, ...updatedCompanyData, parent_company_name: parent_company?.company_name || "N/A" }
                            : company
                    )
                );
                successMessage(response.message || "Company updated successfully!");
            } else {
                errorMessage(response?.message || "Failed to update company. Please try again.");
            }
        } catch (error) {
            console.error("Error updating company:", error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    // Delete Company
    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));

        if (itemToDelete) {
            try {
                const response = await deleteCompany(itemToDelete.row.company_id);

                if (response?.status) {
                    setCompanies((prevCompanies) =>
                        prevCompanies.filter((company) => company.company_id !== itemToDelete.row.company_id)
                    );
                    successMessage(response.message || "Company deleted successfully!");
                } else {
                    errorMessage(response?.message || "Failed to delete company. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting company:", error);
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred. Please try again.');
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <div>
                            <div className='d-flex justify-end'>
                                <div className='d-flex justify-between w-100'>
                                    <TextField
                                        className='w-25'
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '100%', width: '100%' }} className="ag-theme-alpine m-t-20">
                            <GridTable
                                rows={companies}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.company_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                companyName: editCompany ? editCompany.company_name : "",
                                companyLogo: editCompany ? editCompany.company_logo : null,
                                displayName: editCompany ? editCompany.display_name : "",
                                gstNumber: editCompany ? editCompany.gst_number : null,
                                contactNo: editCompany ? editCompany.contact_no : null,
                                address: editCompany ? editCompany.address : null,
                                addParentCompany: !!editCompany?.parent_company_id,
                                parentCompanyId: editCompany ? editCompany.parent_company_id : null,
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue,
                            }) => (
                                <>
                                    <Dialog
                                        open={isDialogOpen}
                                        onClose={() => closeDialog(resetForm)}
                                        onConfirm={handleSubmit}
                                        title={
                                            !loading.update && !loading.create
                                                ? editCompany
                                                    ? "Edit Company"
                                                    : "Add New Company"
                                                : ""
                                        }
                                        confirmButtonText={
                                            !loading.update && !loading.create
                                                ? editCompany
                                                    ? "Update"
                                                    : "Add"
                                                : ""
                                        }
                                        cancelButtonText={!loading.update && !loading.create ? "Cancel" : ""}
                                        icon={<BusinessIcon />}
                                        message={
                                            <Box padding={4}>
                                                {loading.update || loading.create ? (
                                                    <Loader color="primary" size={40} />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="companyName"
                                                                    name="companyName"
                                                                    label="Company Name"
                                                                    required
                                                                    value={values.companyName}
                                                                    onChange={handleChange}
                                                                    error={touched.companyName && Boolean(errors.companyName)}
                                                                    helperText={touched.companyName && errors.companyName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="displayName"
                                                                    name="displayName"
                                                                    label="Display Name"
                                                                    required
                                                                    value={values.displayName}
                                                                    onChange={handleChange}
                                                                    error={touched.displayName && Boolean(errors.displayName)}
                                                                    helperText={touched.displayName && errors.displayName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="companyLogo"
                                                                    name="companyLogo"
                                                                    label="Company Logo"
                                                                    value={values.companyLogo ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.companyLogo && Boolean(errors.companyLogo)}
                                                                    helperText={touched.companyLogo && errors.companyLogo}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="gstNumber"
                                                                    name="gstNumber"
                                                                    label="GST Number"
                                                                    value={values.gstNumber ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.gstNumber && Boolean(errors.gstNumber)}
                                                                    helperText={touched.gstNumber && errors.gstNumber}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="contactNo"
                                                                    name="contactNo"
                                                                    label="Contact Number"
                                                                    value={values.contactNo ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.contactNo && Boolean(errors.contactNo)}
                                                                    helperText={touched.contactNo && errors.contactNo}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="address"
                                                                    name="address"
                                                                    label="Address"
                                                                    value={values.address ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.address && Boolean(errors.address)}
                                                                    helperText={touched.address && errors.address}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={values.addParentCompany}
                                                                            onChange={(e) => setFieldValue("addParentCompany", e.target.checked)}
                                                                        />
                                                                    }
                                                                    label="Add Parent Company"
                                                                />
                                                            </Grid>

                                                            {values.addParentCompany && companies?.length > 0 && (
                                                                <Grid size={4}>
                                                                    <Dropdown
                                                                        options={companies.map((company) => ({
                                                                            id: company.company_id,
                                                                            name: company.company_name,
                                                                        }))}
                                                                        value={values.parentCompanyId ?? ""}
                                                                        onChange={(id) => setFieldValue("parentCompanyId", id)}
                                                                        label="Parent Company"
                                                                        required
                                                                        error={Boolean(touched.parentCompanyId && errors.parentCompanyId)}
                                                                        helperText={
                                                                            touched.parentCompanyId && errors.parentCompanyId
                                                                                ? String(errors.parentCompanyId)
                                                                                : undefined
                                                                        }
                                                                    />
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Form>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Company" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader
                                            color="primary"
                                            size={40}
                                        />
                                    ) : (
                                        "Are you sure you want to delete this company?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );

};

export default Company;
